import { useCallback, useEffect, useState } from "react"
import AdHocCommands from "@/api/adHocCommands"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import { findRoute2gTolls, upsertRoute2gToll } from "@/reducers/routes2gSlice"
import { useTranslation } from "react-i18next"

export const useRouteTolls = (driverJid: string, hasStops: boolean) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [expanded, setExpanded] = useState(false)
  const [loading, setLoading] = useState(false)
  const tolls = useAppSelector(findRoute2gTolls(driverJid))
  const hasTolls = Boolean(tolls && tolls.length > 0)

  useEffect(() => {
    setExpanded(hasTolls)
  }, [driverJid, tolls, hasTolls])

  const toggle = useCallback(async () => {
    if (expanded) {
      setExpanded(false)
      return
    }
    if (hasTolls) {
      setExpanded(true)
      return
    }
    setLoading(true)
    try {
      const result = await AdHocCommands.getTolls(window.XMPP!, driverJid)
      const jsonElement = result
        .getChild("command")
        ?.getChild("json", "urn:xmpp:json:0")
      if (jsonElement) {
        const tollsData = JSON.parse(jsonElement.getText())
        dispatch(upsertRoute2gToll({ jid: driverJid, tolls: tollsData.tolls }))
        setExpanded(true)
      }
    } catch (error) {
      console.error("Failed to fetch tolls:", error)
      const toaster = window.mapToaster ?? window.appToaster
      toaster?.show({
        message: t("maps.useRouteTolls.fetchFailed"),
        intent: "danger",
        timeout: 3000,
      })
    } finally {
      setLoading(false)
    }
  }, [expanded, hasTolls, driverJid, dispatch, t])

  return {
    tolls,
    loading,
    visible: expanded && hasTolls && hasStops,
    label: loading
      ? t("maps.useRouteTolls.loading")
      : expanded
        ? t("maps.useRouteTolls.hide")
        : t("maps.useRouteTolls.show"),
    toggle: hasStops ? toggle : undefined,
  }
}
